import React, { useEffect, useRef } from 'react'
import { KiteAlert, KiteDialog, KiteProgressIndicator, KiteSwitch } from '@kite/react-kite'
import CustomTable, { Spacing } from '../../components/table/Table'
import type { AppDispatch, RootState } from '../../redux/Store'
import { useDispatch, useSelector } from 'react-redux'
import {
  fetchData,
  setError,
  updateFilters,
  updatePagination,
  updateSort,
  updateSyscodeDetails,
} from '../../redux/Syscodes/SyscodesSlice'
import CustomToast from '../../components/toast/CustomToast'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { ISyscodesData } from '../../redux/Syscodes/SyscodesModels'
import { isPowerUserSelector } from '../../redux/User/Selector'

interface ISyscodesTableProps {
  spacing: Spacing
}

const SyscodesTable = ({ spacing = Spacing.Default }: ISyscodesTableProps) => {
  const isInitialMount = useRef(true)
  const dispatch: AppDispatch = useDispatch()
  const [searchParams] = useSearchParams()
  const history = useNavigate()
  const [dialogStatus, setDialogStatus] = React.useState(false)
  const [dialogMessage, setDialogMessage] = React.useState('')
  const {
    syscodesData = [],
    syscodeModifyStatus,
    status,
    error,
    syscodeDetailsError,
    tableFilters,
    tablePagination,
    tablePagination: { PageNumber, RecordsPerPage },
    tableSort,
  } = useSelector((state: RootState) => state.syscodesData)
  const isPowerUser = useSelector(isPowerUserSelector);

  const isLoading = status === 'loading';

  const handleStatusChange = (row: ISyscodesData) => {
    const newStatus = !row.Enabled ? 1 : 0;
    const reqPayload: { [x: string]: any } = {
      SysCode: row.Syscode,
      Enabled: newStatus
    };
    // Setting applied values to URL
    const params = new URLSearchParams()
    Object.keys(reqPayload).forEach((filterKey) => {
      if (reqPayload[filterKey]) {
        params.append(filterKey, reqPayload[filterKey])
      }
    })
    dispatch(
      updateSyscodeDetails(
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        reqPayload,
      ),
    )
  }

  const columns = [
    {
      label: 'Enabled',
      accessorKey: 'Enabled',
      width: '80px',
      render: (row: ISyscodesData) => {
        return <span>
          <KiteSwitch selected={!!row?.Enabled} onClick={() => handleStatusChange(row)} disabled={!isPowerUser}/>

        </span>
      },
    },
    {
      label: 'XGL',
      sortKey: 'XGL',
      accessorKey: 'XGL',
      width: '80px',
    },
    {
      label: 'Syscode',
      sortKey: 'Syscode',
      accessorKey: 'Syscode',
      width: '100px',
    },
    {
      label: 'Zone Description',
      sortKey: 'ZoneDescription',
      accessorKey: 'Zone Description',
      width: '200px',
    },
    {
      label: 'Zones',
      sortKey: 'Zones',
      accessorKey: 'Zones',
      width: '100px',
    }
  ]

  const handleSort = (column: string) => {
    const currentSortKey = tableSort?.SortBy
    const sortDirection = tableSort?.SortOrder
    let newSortDirection: any = 'asc'
    if (currentSortKey === column) {
      if (!sortDirection) {
        newSortDirection = 'asc'
      } else if (sortDirection === 'asc') {
        newSortDirection = 'desc'
      } else if (sortDirection === 'desc') {
        newSortDirection = 'asc'
      }
    } else {
      newSortDirection = 'asc'
    }
    dispatch(updateSort({ SortBy: column, SortOrder: newSortDirection }))
  }

  useEffect(() => {
    // Default values
    const currentFiltersData: { [x: string]: any } = {
      ...tableFilters
    }
    const currentSortData: { [x: string]: any } = { ...tableSort }
    const currentPaginationData: { [x: string]: any } = { ...tablePagination }
    // Values From URl
    const paramEntries: any = searchParams.entries()
    for (const entry of paramEntries) {
      const [param, value] = entry
      if (value) {
        if (param === 'PageNumber' || param === 'RecordsPerPage') {
          currentPaginationData[param] = value
        } else if (param === 'SortBy' || param === 'SortOrder') {
          currentSortData[param] = value
        } else {
          currentFiltersData[param] = value
        }
      }
    }
    dispatch(
      updateFilters({
        ...currentFiltersData
      }),
    )
    dispatch(updateSort({ ...currentSortData }))
    dispatch(updatePagination({ ...currentPaginationData }))
  }, [])

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }
    fetchSyscodes();
  }, [
    JSON.stringify({
      ...tableFilters,
      ...tablePagination,
      ...tableSort,
    }),
    isInitialMount.current
  ])

  const fetchSyscodes = () => {
    const reqPayload: { [x: string]: any } = {
      ...tableFilters,
      ...tableSort,
      ...tablePagination,
    }
    // Setting applied values to URL
    const params = new URLSearchParams()
    Object.keys(reqPayload).forEach((filterKey) => {
      if (reqPayload[filterKey]) {
        params.append(filterKey, reqPayload[filterKey])
      }
    })
    history({ search: params.toString() }, { replace: true })
    dispatch(
      fetchData(
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        reqPayload,
      ),
    )
  }

  useEffect(() => {
    if (syscodeModifyStatus === 'succeeded') {
      setDialogStatus(true)
      const message = 'Updated SysCode successfully'
      setDialogMessage(message)
    }
  }, [syscodeModifyStatus])

  const handleClearError = () => {
    dispatch(setError(''))
  }

  const handlePagination = (name: string) => (value: number) => {
    dispatch(
      updatePagination({
        ...tablePagination,
        [name]: value,
        ...(name === 'RecordsPerPage' && { PageNumber: 1 }),
      }),
    )
  }

  const handleDialogClose = () => {
    setDialogStatus(false)
    setDialogMessage('')
  }

  const totalItems = Number(syscodesData[0]?.MaxCount || 0)

  return (
    <>
      {error && (
        <div className='alert-element'>
          <KiteAlert
            onClose={handleClearError}
            autoFocus={false}
            level='page'
            type='error'
            description={error}
            dismissible
          />
        </div>
      )}

      <CustomTable
        columns={columns}
        currentSortKey={tableSort?.SortBy}
        sortDirection={tableSort?.SortOrder}
        onSort={handleSort}
        data={syscodesData || []}
        spacing={spacing}
        loading={isLoading}
        currentPage={PageNumber}
        onPageChange={handlePagination('PageNumber')}
        itemsPerPage={RecordsPerPage}
        onItemsPerPageChange={handlePagination('RecordsPerPage')}
        totalItems={totalItems}
      />
      {syscodeDetailsError && (
        <div className='alert-element'>
          <KiteAlert
            autoFocus={false}
            level='page'
            type='error'
            description={syscodeDetailsError}
          />
        </div>
      )}
      <KiteDialog
        id='confirmDialog'
        className='confirm-dialog'
        open={dialogStatus}
        title='Success'
        onClose={handleDialogClose}
        primaryBtnLabel='Ok'
        onPrimaryBtnClick={handleDialogClose}
      >
        <div className='kite-dialog__content-group'>{dialogMessage}</div>
      </KiteDialog>
      {isLoading && <KiteProgressIndicator useOverlay id='kp1' message='' title='Loading' />}
    </>
  )
}

export default SyscodesTable
